import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from '@emotion/styled';
import TextField from '../../../../components/forms/TextField';
import { color_text_link, color_shades_darkest, color_shades_lightest, color_shades_lighter, color_shades_light, color_shades_dark, color_shades_darker } from '../../../../constants/colors';
import { ALL_TAGS_AND_CATEGORIES } from '../../../../constants/analyticsConstants/generativeCallReview';

interface NotificationEmail {
    id: string;
    email: string;
    requiredTags: string[];
    excludedTags: string[];
    customTags: string[];
    customTagsInput: string; // Store the raw input string
}

interface NotificationEmailsManagerProps {
    value: string;
    onChange: (value: string) => void;
    onBlur: () => void;
    fieldContainerStyle?: any;
}

const NotificationEmailsManager: React.FC<NotificationEmailsManagerProps> = ({
    value,
    onChange,
    onBlur,
    fieldContainerStyle
}) => {
    const [emailConfigs, setEmailConfigs] = useState<NotificationEmail[]>([]);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const emailConfigsRef = useRef<NotificationEmail[]>([]);

    // Update ref whenever emailConfigs changes
    useEffect(() => {
        emailConfigsRef.current = emailConfigs;
    }, [emailConfigs]);

    // Debounced update function to prevent excessive onChange calls
    const debouncedUpdateValue = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            const configs = emailConfigsRef.current;
            const emailStrings = configs.map(config => {
                // Combine all tags and deduplicate
                const allTags: string[] = [];
                const seenTags = new Set<string>();

                // Add required tags
                config.requiredTags.forEach(tag => {
                    if (!seenTags.has(tag) && !seenTags.has(`!${tag}`)) {
                        allTags.push(tag);
                        seenTags.add(tag);
                    }
                });

                // Add excluded tags with ! prefix
                config.excludedTags.forEach(tag => {
                    const excludedTag = `!${tag}`;
                    if (!seenTags.has(excludedTag) && !seenTags.has(tag)) {
                        allTags.push(excludedTag);
                        seenTags.add(excludedTag);
                    }
                });

                // Add custom tags (may already have ! prefix)
                config.customTags.forEach(tag => {
                    const normalizedTag = tag.startsWith('!') ? tag.substring(1) : tag;
                    if (!seenTags.has(tag) && !seenTags.has(normalizedTag) && (!tag.startsWith('!') || !seenTags.has(normalizedTag))) {
                        allTags.push(tag);
                        seenTags.add(tag);
                        if (tag.startsWith('!')) {
                            seenTags.add(normalizedTag);
                        }
                    }
                });

                if (allTags.length === 0) {
                    return config.email || ''; // Return empty string for new emails without address
                } else {
                    return config.email ? `${config.email}(${allTags.join(',')})` : `(${allTags.join(',')})`;
                }
            }).filter(emailString => emailString !== ''); // Only filter out completely empty strings

            console.log(emailStrings.join(','));
            onChange(emailStrings.join(','));
        }, 300); // 300ms debounce
    }, [onChange]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    // Parse the value string into email configurations
    useEffect(() => {
        if (!value) {
            setEmailConfigs([]);
            return;
        }

        const configs: NotificationEmail[] = [];

        // More sophisticated parsing to handle email(tag1,tag2,tag3) format
        let currentIndex = 0;
        let emailIndex = 0;

        while (currentIndex < value.length) {
            let nextComma = value.indexOf(',', currentIndex);
            let nextParen = value.indexOf('(', currentIndex);

            // If no more commas, take the rest of the string
            if (nextComma === -1) {
                nextComma = value.length;
            }

            let emailPart;

            if (nextParen !== -1 && nextParen < nextComma) {
                // This email has conditions - find the matching closing parenthesis
                let parenCount = 1;
                let searchIndex = nextParen + 1;

                while (parenCount > 0 && searchIndex < value.length) {
                    if (value[searchIndex] === '(') {
                        parenCount++;
                    } else if (value[searchIndex] === ')') {
                        parenCount--;
                    }
                    searchIndex++;
                }

                emailPart = value.substring(currentIndex, searchIndex).trim();
                currentIndex = searchIndex + 1; // Skip the comma after the closing paren
            } else {
                // Simple email without conditions
                emailPart = value.substring(currentIndex, nextComma).trim();
                currentIndex = nextComma + 1;
            }

            if (emailPart) {
                const parenIndex = emailPart.indexOf('(');

                if (parenIndex === -1) {
                    // Simple email without conditions
                    configs.push({
                        id: `email-${emailIndex}`,
                        email: emailPart,
                        requiredTags: [],
                        excludedTags: [],
                        customTags: [],
                        customTagsInput: ''
                    });
                } else {
                    // Email with conditions
                    const email = emailPart.substring(0, parenIndex);
                    const conditionsPart = emailPart.substring(parenIndex + 1);
                    const endParen = conditionsPart.lastIndexOf(')');
                    const conditions = endParen !== -1 ? conditionsPart.substring(0, endParen) : conditionsPart;

                    const requiredTags: string[] = [];
                    const excludedTags: string[] = [];
                    const customTags: string[] = [];

                    conditions.split(',').forEach(condition => {
                        const trimmed = condition.trim();
                        if (trimmed.startsWith('!')) {
                            const tag = trimmed.substring(1);
                            if (ALL_TAGS_AND_CATEGORIES.includes(tag)) {
                                excludedTags.push(tag);
                            } else {
                                customTags.push(trimmed); // Keep the ! for custom excluded tags
                            }
                        } else {
                            if (ALL_TAGS_AND_CATEGORIES.includes(trimmed)) {
                                requiredTags.push(trimmed);
                            } else {
                                customTags.push(trimmed);
                            }
                        }
                    });

                    if (email) {
                        configs.push({
                            id: `email-${emailIndex}`,
                            email,
                            requiredTags,
                            excludedTags,
                            customTags,
                            customTagsInput: customTags.join(', ')
                        });
                    }
                }
                emailIndex++;
            }
        }

        setEmailConfigs(configs);
    }, [value]);

    // Convert email configurations back to string format
    const updateValue = useCallback(() => {
        debouncedUpdateValue();
    }, [debouncedUpdateValue]);

    // Immediate update for critical operations (add/remove)
    const immediateUpdateValue = useCallback((configs: NotificationEmail[]) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        const emailStrings = configs.map(config => {
            // Combine all tags and deduplicate
            const allTags: string[] = [];
            const seenTags = new Set<string>();

            // Add required tags
            config.requiredTags.forEach(tag => {
                if (!seenTags.has(tag) && !seenTags.has(`!${tag}`)) {
                    allTags.push(tag);
                    seenTags.add(tag);
                }
            });

            // Add excluded tags with ! prefix
            config.excludedTags.forEach(tag => {
                const excludedTag = `!${tag}`;
                if (!seenTags.has(excludedTag) && !seenTags.has(tag)) {
                    allTags.push(excludedTag);
                    seenTags.add(excludedTag);
                }
            });

            // Add custom tags (may already have ! prefix)
            config.customTags.forEach(tag => {
                const normalizedTag = tag.startsWith('!') ? tag.substring(1) : tag;
                if (!seenTags.has(tag) && !seenTags.has(normalizedTag) && (!tag.startsWith('!') || !seenTags.has(normalizedTag))) {
                    allTags.push(tag);
                    seenTags.add(tag);
                    if (tag.startsWith('!')) {
                        seenTags.add(normalizedTag);
                    }
                }
            });

            if (allTags.length === 0) {
                return config.email || ''; // Return empty string for new emails without address
            } else {
                return config.email ? `${config.email}(${allTags.join(',')})` : `(${allTags.join(',')})`;
            }
        }).filter(emailString => emailString !== ''); // Only filter out completely empty strings

        console.log(emailStrings.join(','));
        onChange(emailStrings.join(','));
    }, [onChange]);

    const addEmailConfig = () => {
        const newConfig: NotificationEmail = {
            id: `email-${Date.now()}`,
            email: '',
            requiredTags: [],
            excludedTags: [],
            customTags: [],
            customTagsInput: ''
        };
        const updatedConfigs = [...emailConfigs, newConfig];
        setEmailConfigs(updatedConfigs);
        // Don't call immediateUpdateValue here since the email is empty
        // The onChange will be called when user actually enters an email
    };

    const removeEmailConfig = (id: string) => {
        const updatedConfigs = emailConfigs.filter(config => config.id !== id);
        setEmailConfigs(updatedConfigs);
        immediateUpdateValue(updatedConfigs);
    };

    const updateEmailConfig = (id: string, updates: Partial<NotificationEmail>) => {
        setEmailConfigs(prevConfigs =>
            prevConfigs.map(config =>
                config.id === id ? { ...config, ...updates } : config
            )
        );
    };

    const handleTagSelection = (configId: string, field: 'requiredTags' | 'excludedTags', tagValue: string) => {
        const config = emailConfigs.find(c => c.id === configId);
        if (!config) return;

        const currentTags = config[field];
        const isSelected = currentTags.includes(tagValue);
        const oppositeField = field === 'requiredTags' ? 'excludedTags' : 'requiredTags';
        const oppositeTags = config[oppositeField];

        let updatedTags;
        let updatedOppositeTags = oppositeTags;

        if (isSelected) {
            // Unselecting the tag
            updatedTags = currentTags.filter(tag => tag !== tagValue);
        } else {
            // Selecting the tag - first remove it from the opposite list if it exists
            updatedOppositeTags = oppositeTags.filter(tag => tag !== tagValue);
            updatedTags = [...currentTags, tagValue];
        }

        // Update both fields to ensure the tag is only in one list
        updateEmailConfig(configId, {
            [field]: updatedTags,
            [oppositeField]: updatedOppositeTags
        });

        // Use debounced update for tag selections
        updateValue();
    };

    return (
        <Container>
            <Label>Notification Emails</Label>
            <Description>
                Set up email addresses and rules for call notifications to this number. By default, notifications are sent to all listed emails. Use tags to control which emails receive specific notifications.
            </Description>

            {emailConfigs.map((config, index) => (
                <EmailConfigContainer key={config.id}>
                    <EmailConfigHeader>
                        <EmailConfigTitle>Email Configuration {index + 1}</EmailConfigTitle>
                        {emailConfigs.length > 1 && (
                            <RemoveButton
                                type="button"
                                onClick={() => removeEmailConfig(config.id)}
                            >
                                Remove
                            </RemoveButton>
                        )}
                    </EmailConfigHeader>

                    <EmailTextField
                        name={`email-${config.id}`}
                        value={config.email}
                        label="Email Address"
                        placeholder="example@voicify.com"
                        onChange={(e) => updateEmailConfig(config.id, { email: e.target.value })}
                        onBlur={() => {
                            // Only trigger onChange when user finishes editing the email
                            updateValue();
                            onBlur();
                        }}
                        fieldContainerStyle={fieldContainerStyle}
                    />

                    <TagSection>
                        <TagSectionTitle>Required Tags</TagSectionTitle>
                        <Description>Email sent when ALL of these are present:</Description>
                        <TagGrid>
                            {ALL_TAGS_AND_CATEGORIES.map(tag => (
                                <TagCheckbox key={`required-${tag}`}>
                                    <input
                                        id={`required-${config.id}-${tag}`}
                                        type="checkbox"
                                        checked={config.requiredTags.includes(tag)}
                                        onChange={() => handleTagSelection(config.id, 'requiredTags', tag)}
                                    />
                                    <label htmlFor={`required-${config.id}-${tag}`}>{tag}</label>
                                </TagCheckbox>
                            ))}
                        </TagGrid>
                    </TagSection>

                    <TagSection>
                        <TagSectionTitle>Excluded Tags</TagSectionTitle>
                        <Description>Email NOT sent when ANY of these are present:</Description>
                        <TagGrid>
                            {ALL_TAGS_AND_CATEGORIES.map(tag => (
                                <TagCheckbox key={`excluded-${tag}`}>
                                    <input
                                        id={`excluded-${config.id}-${tag}`}
                                        type="checkbox"
                                        checked={config.excludedTags.includes(tag)}
                                        onChange={() => handleTagSelection(config.id, 'excludedTags', tag)}
                                    />
                                    <label htmlFor={`excluded-${config.id}-${tag}`}>{tag}</label>
                                </TagCheckbox>
                            ))}
                        </TagGrid>
                    </TagSection>

                    <CustomTagTextField
                        name={`custom-tag-${config.id}`}
                        value={config.customTagsInput}
                        label="Custom Tags"
                        placeholder="Enter custom tags separated by commas (prefix with ! to exclude, e.g., CUSTOM_TAG1, !CUSTOM_TAG2)"
                        onChange={(e) => {
                            // Just update the input string, don't process tags yet
                            updateEmailConfig(config.id, { customTagsInput: e.target.value });
                        }}
                        onBlur={() => {
                            // Process the tags when user finishes editing
                            const customTagsString = config.customTagsInput;
                            const customTags = customTagsString
                                .split(',')
                                .map(tag => tag.trim())
                                .filter(tag => tag.length > 0);

                            const updatedConfigs = emailConfigs.map(c =>
                                c.id === config.id ? { ...c, customTags } : c
                            );
                            setEmailConfigs(updatedConfigs);
                            immediateUpdateValue(updatedConfigs); // Use immediate update for blur
                            onBlur();
                        }}
                        fieldContainerStyle={fieldContainerStyle}
                    />
                </EmailConfigContainer>
            ))}

            <AddButton type="button" onClick={addEmailConfig}>
                + Add Email Configuration
            </AddButton>
        </Container>
    );
};

const Container = styled.div`
    margin-bottom: 26px;
`;

const Label = styled.p`
    font-weight: 400;
    font-size: 14px;
    color: ${color_shades_darkest};
    margin: 0 0 8px 0;
`;

const Description = styled.p`
    font-size: 14px;
    color: ${color_shades_darker};
    margin: 0 0 16px 0;
`;

const EmailConfigContainer = styled.div`
    border: 1px solid #e1e1e1;
    border-radius: 4px;
    padding: 16px;
    margin-bottom: 16px;
    background-color: #fafafa;
`;

const EmailConfigHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
`;

const EmailConfigTitle = styled.h4`
    font-size: 14px;
    font-weight: 500;
    color: ${color_shades_darkest};
    margin: 0;
`;

const EmailTextField = styled(TextField)`
    margin-bottom: 16px;
    font-size: 14px;
`;

const CustomTagTextField = styled(TextField)`
    margin-bottom: 0;
    font-size: 14px;
`;

const TagSection = styled.div`
    margin: 24px 0;
`;

const TagSectionTitle = styled.p`
    font-size: 14px;
    font-weight: 500;
    color: ${color_shades_darkest};
    margin: 0 0 8px 0;
`;

const TagGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

const TagCheckbox = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 6px;
    flex-shrink: 0;
    
    input[type="checkbox"] {
        margin: 0;
        margin-top: 2px;
        flex-shrink: 0;
    }
    
    label {
        font-size: 12px;
        color: ${color_shades_darkest};
        margin: 0;
        cursor: pointer;
        white-space: nowrap;
        line-height: 1.3;
    }
`;

const RemoveButton = styled.button`
    background: none;
    border: none;
    color: ${color_text_link};
    font-size: 14px;
    cursor: pointer;
    padding: 4px 8px;

    &:hover {
        color: #999;
    }
`;

const AddButton = styled.button`
    background: none;
    border: none;
    color: ${color_text_link};
    font-size: 14px;
    cursor: pointer;
    padding: 8px 0;
    &:hover {
        color: #999;
    }
`;

export default NotificationEmailsManager;
